import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Download, FileText, FileSpreadsheet, Loader2 } from 'lucide-react';
import Button from '../ui/Button';
import trafficManagerService from '../../services/trafficManagerService';

/** Os últimos meses (o atual primeiro), em YYYY-MM. */
const ultimosMeses = (n: number) => {
  const out: string[] = [];
  const d = new Date();
  for (let i = 0; i < n; i++) {
    const x = new Date(d.getFullYear(), d.getMonth() - i, 1);
    out.push(`${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}`);
  }
  return out;
};

/** Exportar o relatório do mês: PDF para enviar, Excel para analisar. */
const ExportMenu: React.FC<{ managerId: string }> = ({ managerId }) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [mes, setMes] = useState(ultimosMeses(1)[0]);
  const [baixando, setBaixando] = useState<'pdf' | 'xlsx' | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const fora = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', fora);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', fora); document.removeEventListener('keydown', esc); };
  }, [open]);
  const nomeMes = (m: string) => new Intl.DateTimeFormat(i18n.language, { month: 'long', year: 'numeric' }).format(new Date(`${m}-15T12:00:00`));
  const baixar = async (formato: 'pdf' | 'xlsx') => {
    setBaixando(formato);
    try {
      await trafficManagerService.downloadReport(managerId, mes, formato, (i18n.language || 'pt').slice(0, 3).replace(/-.*/, ''));
      setOpen(false);
    } catch {
      toast.error(t('trafficManager.report.error', 'Não foi possível gerar o relatório'));
    } finally { setBaixando(null); }
  };
  return (
    <div className="relative" ref={ref}>
      <Button variant="secondary" size="sm" className="h-10" onClick={() => setOpen((v) => !v)} aria-expanded={open} aria-haspopup="dialog" leftIcon={<Download className="w-4 h-4" />}>
        <span className="hidden sm:inline">{t('trafficManager.report.button', 'Relatório')}</span>
      </Button>
      {open && (
        <div role="dialog" aria-label={t('trafficManager.report.title', 'Exportar relatório') as string}
          className="absolute right-0 z-30 mt-2 w-72 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl p-4">
          <div className="text-sm font-semibold text-gray-900 dark:text-white">{t('trafficManager.report.title', 'Exportar relatório')}</div>
          <p className="text-xs text-gray-500 mt-0.5 mb-3">{t('trafficManager.report.help', 'Esperado x real contra a média, campanhas e cada mudança do robô com o efeito medido.')}</p>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1" htmlFor="tm-report-month">{t('trafficManager.report.month', 'Mês')}</label>
          <select id="tm-report-month" value={mes} onChange={(e) => setMes(e.target.value)}
            className="w-full px-3 py-2 text-base sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white mb-3">
            {ultimosMeses(6).map((m) => <option key={m} value={m}>{nomeMes(m)}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" disabled={!!baixando} onClick={() => baixar('pdf')} className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60">
              {baixando === 'pdf' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}PDF
            </button>
            <button type="button" disabled={!!baixando} onClick={() => baixar('xlsx')} className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-60">
              {baixando === 'xlsx' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}Excel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExportMenu;
